/**
 * Task #1511 — tipi shared per il dominio Listini POS.
 *
 * Centralizza le shape scambiate fra backend (`PosPriceListService` +
 * router `pricelists.routes.ts`) e frontend (cassa POS, pagina
 * `PriceLists`, modal selezione listino, settings tasti rapidi).
 */

import { z } from 'zod';
import {
  INSTALLMENT_REPAYMENTS,
  PAYMENT_MODE_ADMIN_EXPECTATIONS,
  PAYMENT_MODE_CREDIT_HOLDERS,
  PAYMENT_MODE_ERROR_CODES,
  type InstallmentRepayment,
  type PaymentModeAdminExpectation,
  type PaymentModeAccountingExpectation,
  type PaymentModeCreditHolder,
  type PaymentModeErrorCode,
} from './payment-modes';

/**
 * Task #3275 — tipi di listino poliforme (architettura type-driven).
 * Il campo guida quali strutture figlie sono ammesse:
 * - `standard`: solo righe `pos_price_list_items`.
 * - `canvas`: tariffe pure in `pos_price_list_canvas_items`.
 * - `device_promo`: composizioni SENZA canvas (canvas_item_id NULL).
 * - `bundle`: composizioni CON canvas obbligatorio.
 * Estendere qui + migration CHECK quando si introducono nuovi tipi.
 */
export const PRICE_LIST_TYPES = ['standard', 'canvas', 'device_promo', 'bundle'] as const;
export type PriceListType = (typeof PRICE_LIST_TYPES)[number];

/**
 * Task #3291 — label UI evergreen (unica fonte per gate, badge e modali).
 * L'enum DB resta `standard|canvas|device_promo|bundle`; un futuro deploy
 * brand-specific può sostituire le label con un solo override qui.
 */
export const PRICE_LIST_TYPE_LABELS: Record<PriceListType, string> = {
  standard: 'Listino base',
  canvas: 'Listino Canvas',
  device_promo: 'Promozione prodotto',
  bundle: 'Offerta combinata',
};

/**
 * Regole di testata per tipologia (unica fonte FE+BE+CHECK DB
 * `pos_price_lists_type_rules_check`):
 * - insegna: obbligatoria su canvas e bundle; facoltativa su base/promo
 *   (vuota = generico, proposto su ogni insegna);
 * - fornitore: obbligatorio su base/promo/bundle, non previsto sul Canvas;
 * - driver di business: derivato (mai digitato) solo su canvas e bundle.
 */
export interface PriceListTypeRules {
  supplier: 'required' | 'forbidden';
  brand: 'required' | 'optional';
  /** Il tipo porta un driver derivato (Canvas: dalle tariffe; bundle: dalla tariffa Canvas delle composizioni). */
  derived_driver: boolean;
  /** Target B2C/B2B binario + almeno un canale (perimetro delle tariffe Canvas). */
  canvas_scope: boolean;
}
export const PRICE_LIST_TYPE_RULES: Record<PriceListType, PriceListTypeRules> = {
  standard: { supplier: 'required', brand: 'optional', derived_driver: false, canvas_scope: false },
  canvas: { supplier: 'forbidden', brand: 'required', derived_driver: true, canvas_scope: true },
  device_promo: { supplier: 'required', brand: 'optional', derived_driver: false, canvas_scope: false },
  bundle: { supplier: 'required', brand: 'required', derived_driver: true, canvas_scope: false },
};

export interface PriceListHeaderRuleInput {
  list_type: PriceListType;
  supplier_id: string | null;
  retail_brand_id: string | null;
  customer_scope: PriceListCustomerScope;
  channel_ids: readonly string[];
}
export interface PriceListHeaderRuleIssue {
  field: 'supplier_id' | 'retail_brand_id' | 'customer_scope' | 'channel_ids';
  message: string;
}

/** Violazioni delle regole di testata (vuoto = testata valida). Stessa logica nel form e nell'API. */
export function priceListHeaderRuleErrors(input: PriceListHeaderRuleInput): PriceListHeaderRuleIssue[] {
  const rules = PRICE_LIST_TYPE_RULES[input.list_type];
  const label = PRICE_LIST_TYPE_LABELS[input.list_type];
  const issues: PriceListHeaderRuleIssue[] = [];
  if (rules.supplier === 'required' && !input.supplier_id) issues.push({ field: 'supplier_id', message: `${label}: il fornitore è obbligatorio` });
  if (rules.supplier === 'forbidden' && input.supplier_id) issues.push({ field: 'supplier_id', message: `${label}: il fornitore non è previsto (il perimetro è insegna + target)` });
  if (rules.brand === 'required' && !input.retail_brand_id) issues.push({ field: 'retail_brand_id', message: `${label}: l'insegna è obbligatoria` });
  if (rules.canvas_scope) {
    if (input.customer_scope === 'mixed') issues.push({ field: 'customer_scope', message: `${label}: target B2C o B2B obbligatorio (mai misto)` });
    if (input.channel_ids.length === 0) issues.push({ field: 'channel_ids', message: `${label}: almeno un canale obbligatorio` });
  }
  return issues;
}

/** Task #3275 — ambito clientela del listino. */
export const PRICE_LIST_CUSTOMER_SCOPES = ['consumer', 'business', 'mixed'] as const;
export type PriceListCustomerScope = (typeof PRICE_LIST_CUSTOMER_SCOPES)[number];

/**
 * Listini Canvas: target binario (B2C o B2B), mai misto. Unica fonte per il
 * gate server-side (CHECK `pos_price_lists_canvas_scope_binary_check`) e per
 * il selettore della testata.
 */
export const CANVAS_PRICE_LIST_CUSTOMER_SCOPES = ['consumer', 'business'] as const;
export type CanvasPriceListCustomerScope = (typeof CANVAS_PRICE_LIST_CUSTOMER_SCOPES)[number];

/** Label UI dell'ambito clientela (unica fonte: niente ternari locali). */
export const PRICE_LIST_CUSTOMER_SCOPE_LABELS: Record<PriceListCustomerScope, string> = {
  consumer: 'Clienti privati (B2C)',
  business: 'Clienti business (B2B)',
  mixed: 'Clienti misti',
};

/** Target articolo Canvas (b2c|b2b) ↔ ambito clientela del listino (consumer|business). */
export const TARGET_CUSTOMERS_TO_CUSTOMER_SCOPE = {
  b2c: 'consumer',
  b2b: 'business',
} as const satisfies Record<'b2c' | 'b2b', CanvasPriceListCustomerScope>;
export const CUSTOMER_SCOPE_TO_TARGET_CUSTOMERS = {
  consumer: 'b2c',
  business: 'b2b',
} as const satisfies Record<CanvasPriceListCustomerScope, 'b2c' | 'b2b'>;
export const TARGET_CUSTOMERS_LABELS: Record<'b2c' | 'b2b', string> = {
  b2c: 'B2C',
  b2b: 'B2B',
};

/**
 * L'addebito (rimborso rate cliente→ente) NON è una dimensione globale del
 * listino: il template dell'insegna dichiara gli addebiti ammessi, la
 * composizione bundle sceglie quello effettivo (`installment_repayment`).
 * Snapshot vendita e commissioning lo leggono dalla composizione.
 */
export {
  INSTALLMENT_REPAYMENTS,
  INSTALLMENT_REPAYMENT_LABELS,
  INSTALLMENT_REPAYMENT_SHORT_LABELS,
  installmentRepaymentLabel,
} from './payment-modes';
export type { InstallmentRepayment } from './payment-modes';

/**
 * Task #3275/#4322 — flussi amministrativi attesi generati dalla vendita,
 * DERIVATI dalla modalità di pagamento all'insegna: cessione del credito
 * (titolare insegna con attesa cessione, es. VAR) o nota di credito (es.
 * Easypay). L'ente terzo non genera attese (solo match contabile
 * dell'accredito). Elenco estendibile (CHECK DB allineato).
 */
export const ADMIN_EXPECTATION_KINDS = ['credit_note', 'credit_assignment'] as const;
export type AdminExpectationKind = (typeof ADMIN_EXPECTATION_KINDS)[number];
export const ADMIN_EXPECTATION_KIND_LABELS: Record<AdminExpectationKind, string> = {
  credit_assignment: 'Cessione del credito',
  credit_note: 'Nota di credito',
};

/** Task #3275/#4331 — ciclo di vita di un'attesa amministrativa
 * (`cancelled` = annullo vendita/riga/pratica, terminale). */
export const ADMIN_EXPECTATION_STATUSES = ['expected', 'documented', 'reconciled', 'cancelled'] as const;
export type AdminExpectationStatus = (typeof ADMIN_EXPECTATION_STATUSES)[number];
/** Stati raggiungibili da un operatore (l'annullo passa SOLO dai flussi di annullo). */
export const ADMIN_EXPECTATION_OPERATOR_STATUSES = ['documented', 'reconciled'] as const;

/** Task #4331 — riga di vendita con credito ereditato: il reso è vietato
 * (sostituzione/DOA), l'annullo riga cancella pratica/attese/fatti gara. */
export interface CreditLineBlockedForRefund {
  sale_detail_id: string;
  product_name: string;
  payment_mode_name: string | null;
  financing_contract_id: string | null;
  financing_contract_status: string | null;
  /** true se pratica settled o attesa reconciled: nemmeno l'annullo è ammesso (409). */
  terminal: boolean;
  /** true se la pratica è liquidata (settled o accredito confermato). */
  contract_settled: boolean;
  /** true se la riga è già stata annullata (annullo riga non ripetibile, 409). */
  line_cancelled: boolean;
}

/**
 * Task #2926 — azione di salvataggio di un listino esistente: l'utente sceglie
 * se sovrascrivere la versione corrente o creare una nuova versione (snapshot).
 */
export const PRICE_LIST_VERSIONING_ACTIONS = ['update_current', 'new_version'] as const;
export type PriceListVersioningAction = (typeof PRICE_LIST_VERSIONING_ACTIONS)[number];

/** Canale di insegna referenziato da listino o articolo Canvas. */
export const PriceListChannelRefSchema = z.object({
  channel_id: z.string().uuid(),
  retail_brand_id: z.string().uuid(),
  code: z.string().nullable(),
  display_name: z.string().nullable(),
});
export type PriceListChannelRef = z.infer<typeof PriceListChannelRefSchema>;

/**
 * Entità canonica `pos_price_lists` esposta dagli endpoint CRUD
 * (`GET /pricelists`, `GET /pricelists/:id`, response di create/update).
 */
export interface PriceList {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  /** Task #3275 — tipo di listino poliforme (ex `tipologia`). */
  list_type: PriceListType;
  /** Task #3275 — ambito clientela (consumer|business|mixed). */
  customer_scope: PriceListCustomerScope;
  /** Task #3275 — priorità per la risoluzione di listini sovrapposti. */
  priority: number;
  /** Task #3301 — insegna (retail brand) di ambito del listino (FK retail_brands, opzionale). */
  retail_brand_id: string | null;
  /** Canali dell'insegna coperti dal listino (junction `pos_price_list_channels`, SoT). */
  channels: PriceListChannelRef[];
  /** Task #3275/#3324 — faccia SdI (master) di default testata: NOT NULL a DB. */
  default_vat_rate_id: string;
  /** Task #3291 — IVA SdI di default per l'entry fee (scrittura massiva UI sui prodotti composizione; SoT resta per-riga). */
  default_entry_fee_vat_rate_id: string | null;
  /** Task #3324 — riferimento normativo SdI del default testata (FK natura_iva_map). */
  default_sdi_rif_id: number | null;
  /** Task #3324 — riferimento normativo SdI del default entry fee. */
  default_entry_fee_sdi_rif_id: number | null;
  is_default: boolean;
  /** Unico stato operativo della testata: valorizzato = listino archiviato (fuori da cassa, DDT, inventario). */
  archived_at: string | null;
  valid_from: string | null;
  valid_to: string | null;
  /** Fornitore: obbligatorio su base/promo/bundle, assente (NULL) sul Canvas (vedi PRICE_LIST_TYPE_RULES). */
  supplier_id: string | null;
  /** Driver di business DERIVATO (mai digitato): Canvas → categoria delle tariffe, bundle → tariffa Canvas delle composizioni. NULL = nessuna tariffa o tipo senza driver. */
  business_driver_id: string | null;
  business_driver_name: string | null;
  /** Task #2926 — id del listino LOGICO che raggruppa tutte le versioni. */
  group_id: string;
  /** Task #2926 — numero progressivo della versione nel gruppo (1-based). */
  version: number;
  /** Task #2926 — true sulla sola versione corrente del gruppo (false = versione storica, sola consultazione). */
  is_current_version: boolean;
  /** Task #3274/#3285 — organizzazione brand che gestisce il listino (null = tenant-managed). */
  managed_by_org_id: string | null;
  /** Insegne dell'organizzazione che gestisce il listino (vuoto se tenant-managed): l'insegna di testata deve appartenervi. */
  org_brand_ids: string[];
  /** Task #3274 — flag derivato: true se brand-managed (scritture negate al tenant). */
  is_locked?: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Task #2926 — riga della lista versioni di un gruppo logico
 * (`GET /pricelists/:id/versions`): entità `PriceList` + conteggio articoli.
 */
export interface PriceListVersion extends PriceList {
  items_count: number;
}

/**
 * Entità canonica `pos_price_list_items` (riga listino persistita) restituita
 * da `GET /pricelists/:id/items` e dall'upsert.
 */
export interface PriceListItem {
  id: string;
  tenant_id: string;
  price_list_id: string;
  product_id: string;
  supplier_id: string | null;
  /** Task #2935 — SKU del fornitore per la riga (codice articolo lato fornitore). */
  supplier_sku: string | null;
  /** Task #3324 — faccia SdI (master): NOT NULL a DB. */
  vat_rate_id: string;
  /** Task #3324 — riferimento normativo SdI della riga (FK natura_iva_map). */
  sdi_rif_id: number | null;
  price: number;
  discount_percent: number | null;
  unit_price_net: number | null;
  unit_price_gross: number | null;
  unit_vat_amount: number | null;
  max_discount_percent: number | null;
  created_at: string;
  updated_at: string;
}

/** Riga listino arricchita restituita da GET /pricelists/:id/items. */
export interface PriceListItemWithProduct extends PriceListItem {
  canonical_product_id: string;
  cost_basis: 'original_product' | 'canonical_group';
  product_name: string;
  sku: string | null;
  ean: string | null;
  product_nature: string | null;
  /** Aliquota percentuale dal catalogo tenant (parser PG NUMERIC). */
  vat_rate: number | null;
  vat_nature_code: string | null;
  vat_riferimento_normativo: string | null;
  /** Costo medio ponderato d'acquisto dai lotti, in sola lettura. */
  weighted_avg_cost: number | null;
}

/** Associazione `pos_customer_price_lists` (cliente ↔ listino LOGICO).
 *  Task #2926: l'ancora è `group_id` (il listino logico), mentre `price_list_id`
 *  resta l'audit della versione corrente al momento dell'assegnazione/lettura. */
export interface CustomerPriceList {
  customer_id: string;
  price_list_id: string;
  group_id: string;
  tenant_id: string;
  created_at?: string;
}

// Task #4321 — `PosPriceListRow`/`ProductPriceListsResponse` sostituiti dalle
// offerte per composizione (`PosProductOffer`, ./sale-offers).

/**
 * Output canonical di `PosPriceListService.resolvePrice()` —
 * include sia il prezzo grezzo (`price` + sconto) sia il contratto
 * fiscale (gross/net/iva) quando un listino effettivo è risolto.
 */
export interface PriceResolution {
  price: number | string;
  discount_percent: number | string;
  price_list_name?: string;
  price_list_id?: string;
  no_price_list?: true;
  unit_price_gross?: number;
  unit_price_net?: number;
  unit_vat_amount?: number;
  vat_rate?: number;
  vat_rate_id?: string | null;
  max_discount_percent?: number | null;
}

/** Output di `PosPriceListService.resolvePriceForContext()` per ogni prodotto. */
export interface ResolvedPriceForContext {
  product_id: string;
  price: number | null;
  price_list_id: string | null;
  price_list_name: string | null;
  source: 'price_list_override' | 'customer' | 'default' | 'none';
}

/** Esito di `POST /pricelists/:id/customers/bulk`. */
export interface BulkCustomerPriceListResult {
  action: 'assign' | 'remove';
  requested: number;
  affected: number;
  skipped: number;
  invalid?: number;
}

// ====== ZOD INPUT SCHEMAS ======

const NullableString = z.string().trim().min(1).nullable().optional();
const OptionalDate = z.string().date('Data non valida (YYYY-MM-DD)').nullable().optional();
function refinePriceListDates(data: { valid_from?: string | null; valid_to?: string | null }, ctx: z.RefinementCtx): void {
  if (data.valid_from && data.valid_to && data.valid_from > data.valid_to) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['valid_to'], message: 'La fine validità precede l’inizio' });
  }
}
/** Fornitore di testata: la presenza è decisa per tipologia (`assertPriceListHeaderRules`). */
const HeaderSupplier = z.string().uuid().nullable().optional();

/** Task #3275 — campi testata poliformi condivisi tra create e update. */
const PolymorphicHeaderFields = {
  customer_scope: z.enum(PRICE_LIST_CUSTOMER_SCOPES).optional(),
  priority: z.number().int().optional(),
  retail_brand_id: z.string().uuid().nullable().optional(),
  /** Canali dell'insegna coperti dal listino (sostituzione integrale della junction). */
  channel_ids: z.array(z.string().uuid()).optional(),
  default_vat_rate_id: z.string().uuid('Aliquota IVA predefinita obbligatoria').optional(),
  default_entry_fee_vat_rate_id: z.string().uuid().nullable().optional(),
  /** Task #3324 — riferimento normativo SdI del default testata (FK natura_iva_map). */
  default_sdi_rif_id: z.number().int().positive().nullable().optional(),
  /** Task #3324 — riferimento normativo SdI del default entry fee. */
  default_entry_fee_sdi_rif_id: z.number().int().positive().nullable().optional(),
};

export const CreatePriceListSchema = z.object({
  name: z.string().trim().min(1, 'Nome listino obbligatorio'),
  description: NullableString,
  list_type: z.enum(PRICE_LIST_TYPES).optional(),
  is_default: z.boolean().optional(),
  valid_from: OptionalDate,
  valid_to: OptionalDate,
  supplier_id: HeaderSupplier,
  ...PolymorphicHeaderFields,
  default_vat_rate_id: z.string({ required_error: 'Aliquota IVA predefinita obbligatoria' }).uuid('Aliquota IVA predefinita obbligatoria'),
}).superRefine((data, ctx) => {
  refinePriceListDates(data, ctx);
  for (const issue of priceListHeaderRuleErrors({
    list_type: data.list_type ?? 'standard', supplier_id: data.supplier_id ?? null,
    retail_brand_id: data.retail_brand_id ?? null, customer_scope: data.customer_scope ?? 'mixed',
    channel_ids: data.channel_ids ?? [],
  })) ctx.addIssue({ code: z.ZodIssueCode.custom, path: [issue.field], message: issue.message });
});
export type CreatePriceListInput = z.infer<typeof CreatePriceListSchema>;

export const UpdatePriceListSchema = z.object({
  name: z.string().trim().min(1).optional(),
  description: NullableString,
  list_type: z.enum(PRICE_LIST_TYPES).optional(),
  is_default: z.boolean().optional(),
  valid_from: OptionalDate,
  valid_to: OptionalDate,
  supplier_id: HeaderSupplier,
  ...PolymorphicHeaderFields,
}).superRefine(refinePriceListDates);
export type UpdatePriceListInput = z.infer<typeof UpdatePriceListSchema>;

// ====== TASK #3275 — LISTINI POLIFORMI ======

/** Provenienza del canone effettivo della tariffa. */
export const MONTHLY_FEE_SOURCES = ['inherited', 'override'] as const;
export type MonthlyFeeSource = (typeof MONTHLY_FEE_SOURCES)[number];

/**
 * Tariffa canvas del listino (`pos_price_list_canvas_items`) arricchita con i
 * campi derivati dall'articolo: il canone persistito (`monthly_fee`) è NULL
 * quando ereditato live dall'anagrafica; `effective_monthly_fee` è sempre il
 * valore da usare in lettura, lookup POS e vendita.
 */
export const PriceListCanvasItemSchema = z.object({
  id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  price_list_id: z.string().uuid(),
  product_id: z.string().uuid(),
  /** Override persistito (NULL = ereditato dall'anagrafica). */
  monthly_fee: z.string().nullable(),
  entry_fee: z.string().nullable(),
  contract_duration_months: z.number().int().nullable(),
  external_offer_code: z.string().nullable(),
  /** SKU del fornitore per la tariffa (precompilato dalla mappa fornitore/prodotto). */
  supplier_sku: z.string().nullable(),
  notes: z.string().nullable(),
  is_active: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
  // ── Derivati dall'articolo Canvas ──
  product_name: z.string(),
  product_sku: z.string().nullable(),
  product_ean: z.string().nullable(),
  /** Canone di riferimento dell'anagrafica (pos_products.monthly_fee). */
  reference_monthly_fee: z.string().nullable(),
  /** Canone effettivo: override se presente, altrimenti riferimento. */
  effective_monthly_fee: z.string().nullable(),
  monthly_fee_source: z.enum(MONTHLY_FEE_SOURCES),
  product_entry_fee: z.string().nullable(),
  product_contract_duration_months: z.number().int().nullable(),
  product_target_customers: z.enum(['b2c', 'b2b']).nullable(),
  product_retail_brand_id: z.string().uuid().nullable(),
  product_retail_brand_name: z.string().nullable(),
  product_channels: z.array(PriceListChannelRefSchema),
  product_is_active: z.boolean(),
});
export type PriceListCanvasItem = z.infer<typeof PriceListCanvasItemSchema>;

export const CreateCanvasItemSchema = z.object({
  product_id: z.string().uuid('Prodotto canvas obbligatorio'),
  /** NULL/assente = canone ereditato live dall'anagrafica; valorizzato = override. */
  monthly_fee: z.number().nonnegative('Canone mensile non valido').nullable().optional(),
  entry_fee: z.number().nonnegative().nullable().optional(),
  contract_duration_months: z.number().int().positive().nullable().optional(),
  external_offer_code: z.string().trim().max(80).nullable().optional(),
  supplier_sku: z.string().trim().max(120).nullable().optional(),
  notes: z.string().trim().nullable().optional(),
  is_active: z.boolean().optional(),
});
export type CreateCanvasItemInput = z.infer<typeof CreateCanvasItemSchema>;

export const UpdateCanvasItemSchema = CreateCanvasItemSchema.partial();
export type UpdateCanvasItemInput = z.infer<typeof UpdateCanvasItemSchema>;

/** Salvataggio in blocco delle tariffe Canvas (dialog "Aggiungi tariffe Canvas"). */
export const BulkCreateCanvasItemsSchema = z.object({
  items: z.array(CreateCanvasItemSchema).min(1, 'Almeno una tariffa richiesta').max(500),
});
export type BulkCreateCanvasItemsInput = z.infer<typeof BulkCreateCanvasItemsSchema>;

export const BulkCanvasItemErrorSchema = z.object({
  index: z.number().int().nonnegative(),
  product_id: z.string(),
  message: z.string(),
  code: z.string().nullable(),
});
export type BulkCanvasItemError = z.infer<typeof BulkCanvasItemErrorSchema>;

export const BulkCreateCanvasItemsResultSchema = z.object({
  created: z.array(PriceListCanvasItemSchema),
  errors: z.array(BulkCanvasItemErrorSchema),
});
export type BulkCreateCanvasItemsResult = z.infer<typeof BulkCreateCanvasItemsResultSchema>;

/**
 * Articolo Canvas compatibile con un listino (`GET /pricelists/:id/canvas-compatible-products`):
 * stessa insegna, stesso target, almeno un canale in comune, natura canvas,
 * visibilità brand applicata server-side.
 */
export const CanvasCompatibleProductSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  sku: z.string().nullable(),
  ean: z.string().nullable(),
  monthly_fee: z.string().nullable(),
  entry_fee: z.string().nullable(),
  contract_duration_months: z.number().int().nullable(),
  target_customers: z.enum(['b2c', 'b2b']).nullable(),
  retail_brand_id: z.string().uuid().nullable(),
  retail_brand_name: z.string().nullable(),
  channels: z.array(PriceListChannelRefSchema),
  /** SKU fornitore dalla mappa fornitore/prodotto per il fornitore del listino. */
  supplier_sku: z.string().nullable(),
  category_name: z.string().nullable(),
  product_type_name: z.string().nullable(),
  /** Tariffa già presente nel listino per questo articolo. */
  already_in_list: z.boolean(),
});
export type CanvasCompatibleProduct = z.infer<typeof CanvasCompatibleProductSchema>;

/**
 * Snapshot della tariffa Canvas conservato sulla riga di vendita
 * (`pos_sale_detail_canvas_snapshots`): autosufficiente anche se la tariffa
 * o il listino vengono modificati/eliminati dopo la vendita.
 */
export const SaleDetailCanvasSnapshotSchema = z.object({
  id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  sale_id: z.string().uuid(),
  sale_detail_id: z.string().uuid(),
  canvas_item_id: z.string().uuid().nullable(),
  price_list_id: z.string().uuid().nullable(),
  product_id: z.string().uuid(),
  monthly_fee_effective: z.string(),
  monthly_fee_reference: z.string().nullable(),
  monthly_fee_source: z.enum(MONTHLY_FEE_SOURCES),
  entry_fee: z.string().nullable(),
  contract_duration_months: z.number().int().nullable(),
  external_offer_code: z.string().nullable(),
  supplier_sku: z.string().nullable(),
  retail_brand_id: z.string().uuid().nullable(),
  retail_brand_name: z.string().nullable(),
  channel_id: z.string().uuid().nullable(),
  channel_name: z.string().nullable(),
  target_customers: z.enum(['b2c', 'b2b']).nullable(),
  created_at: z.string(),
});
export type SaleDetailCanvasSnapshot = z.infer<typeof SaleDetailCanvasSnapshotSchema>;

/** Componente di composizione (`pos_price_list_composition_products`). */
export interface CompositionProduct {
  id: string;
  tenant_id: string;
  composition_id: string;
  product_item_id: string;
  price: string;
  discount_percent: string | null;
  discount_amount: string | null;
  /** Anticipo device del componente (ex `entry_fee`, rinominato Task #3476). */
  device_advance_amount: string | null;
  installments_count: number | null;
  installment_amount: string | null;
  quantity: number;
  /** Importo finanziato dall'ente (titolare del credito della modalità). */
  financed_amount: string | null;
  /**
   * Promozione prodotto: nota di credito fornitore attesa UNITARIA dichiarata a
   * listino (null = nessuna attesa). Vietata sulle composizioni finanziate.
   */
  supplier_credit_note_unit: string | null;
  created_at: string;
  updated_at: string;
}

/** Riga bundle (`pos_price_list_compositions`) con i componenti annidati. */
export interface PriceListComposition {
  id: string;
  tenant_id: string;
  price_list_id: string;
  canvas_item_id: string | null;
  /** Modalità di pagamento all'insegna: valorizzata SOLO nei listini bundle (null = composizione non finanziata). */
  payment_mode_id: string | null;
  /** Ente finanziante del tenant (obbligatorio se titolare terzo, vietato altrimenti). */
  financing_partner_id: string | null;
  /** Addebito effettivo scelto tra gli ammessi dal template (null senza modalità). */
  installment_repayment: InstallmentRepayment | null;
  name: string | null;
  display_order: number;
  is_active: boolean;
  /** Task #3285 — organizzazione brand che gestisce la composizione (null = tenant-managed). */
  managed_by_org_id: string | null;
  created_at: string;
  updated_at: string;
  // ── Derivati dalla modalità / ente (tutti null senza modalità) ──
  payment_mode_code: string | null;
  payment_mode_name: string | null;
  payment_mode_credit_holder: PaymentModeCreditHolder | null;
  payment_mode_accounting_expectation: PaymentModeAccountingExpectation | null;
  payment_mode_admin_expectation: PaymentModeAdminExpectation | null;
  /** false = modalità disattivata dal master dopo l'aggancio: la composizione va rimappata. */
  payment_mode_is_active: boolean | null;
  financing_partner_name: string | null;
  products: CompositionProduct[];
}

export const CompositionProductInputSchema = z.object({
  product_item_id: z.string().uuid('Riga listino componente obbligatoria'),
  price: z.number().nonnegative('Prezzo della combinazione non valido'),
  discount_percent: z.number().min(0).max(100).nullable().optional(),
  discount_amount: z.number().nonnegative().nullable().optional(),
  device_advance_amount: z.number().nonnegative().nullable().optional(),
  installments_count: z.number().int().positive().nullable().optional(),
  installment_amount: z.number().nonnegative().nullable().optional(),
  quantity: z.number().int().positive().optional(),
  financed_amount: z.number().nonnegative().nullable().optional(),
  supplier_credit_note_unit: z.number().nonnegative('Nota di credito fornitore attesa non valida').nullable().optional(),
});
export type CompositionProductInput = z.infer<typeof CompositionProductInputSchema>;

// ── Task #4333 — regole aritmetiche dei componenti (unica fonte FE+BE) ──

/** Tolleranza (€) per rata sulla regola finanziato ≈ rate × rata (arrotondamento della rata). */
export const COMPOSITION_INSTALLMENT_TOLERANCE_PER_INSTALLMENT = 0.01;
/** Tolleranza (€) sulla regola prezzo netto = anticipo + finanziato. */
export const COMPOSITION_PRICE_TOLERANCE = 0.01;

/**
 * Campi finanziari del componente: finanziato/rate/rata obbligatori con modalità
 * (bundle); senza modalità (device_promo) TUTTI vietati, anticipo compreso
 * (stesso vincolo del trigger DB `check_pos_comp_product_financed`).
 */
export const COMPOSITION_FINANCIAL_FIELDS = ['financed_amount', 'installments_count', 'installment_amount', 'device_advance_amount'] as const;
export type CompositionFinancialField = (typeof COMPOSITION_FINANCIAL_FIELDS)[number];

/** Campi del componente soggetti alle regole condivise (finanziari + NDC fornitore attesa). */
export type CompositionRuleField = CompositionFinancialField | 'supplier_credit_note_unit';

export interface CompositionProductIssue {
  field: CompositionRuleField;
  code: PaymentModeErrorCode;
  message: string;
}

const toCents = (value: number): number => Math.round(value * 100);
const euro = (value: number): string => value.toFixed(2).replace('.', ',');

/** Prezzo netto del componente: prezzo meno lo sconto (percentuale o in euro), mai negativo. */
export function compositionProductNetPrice(
  p: Pick<CompositionProductInput, 'price' | 'discount_percent' | 'discount_amount'>,
): number {
  if (p.discount_percent != null) return Math.max(0, p.price * (1 - p.discount_percent / 100));
  if (p.discount_amount != null) return Math.max(0, p.price - p.discount_amount);
  return p.price;
}

/**
 * Regole di coerenza del componente rispetto alla modalità della composizione
 * (fail-closed, applicate alla SCRITTURA; i dati storici non sono rivalutati):
 * - con modalità (bundle, sempre finanziata): finanziato > 0, rate e rata obbligatori;
 *   |finanziato − rate × rata| ≤ 0,01 × rate; prezzo netto = anticipo + finanziato (±0,01);
 * - senza modalità (device_promo): finanziato/rate/rata/anticipo NULL (anche 0 è
 *   rifiutato: il trigger DB non distingue 0 da un valore, e la richiesta non deve
 *   passare la validazione per poi fallire sul vincolo);
 * - NDC fornitore attesa (`supplier_credit_note_unit`): ammessa SOLO senza modalità
 *   (stesso vincolo del trigger DB); con modalità è rifiutata.
 * Ritorna un issue per campo (path stabile per l'editor).
 */
export function compositionProductCreditIssues(
  p: CompositionProductInput,
  financed: boolean,
): CompositionProductIssue[] {
  const issues: CompositionProductIssue[] = [];
  if (!financed) {
    const forbidden: Record<CompositionFinancialField, string> = {
      financed_amount: 'importo finanziato',
      installments_count: 'numero rate',
      installment_amount: 'rata',
      device_advance_amount: 'anticipo device',
    };
    for (const field of COMPOSITION_FINANCIAL_FIELDS) {
      const value = p[field];
      if (value != null) {
        issues.push({ field, code: PAYMENT_MODE_ERROR_CODES.FINANCED_NOT_APPLICABLE, message: `Composizione non finanziata: ${forbidden[field]} non applicabile (lasciare vuoto)` });
      }
    }
    return issues;
  }
  if (p.supplier_credit_note_unit != null) {
    issues.push({ field: 'supplier_credit_note_unit', code: PAYMENT_MODE_ERROR_CODES.SUPPLIER_CREDIT_NOTE_NOT_APPLICABLE, message: 'Composizione finanziata: la nota di credito fornitore attesa non è applicabile (lasciare vuoto)' });
  }
  const financedAmount = p.financed_amount;
  const count = p.installments_count;
  const installment = p.installment_amount;
  if (financedAmount == null || !(financedAmount > 0)) {
    issues.push({ field: 'financed_amount', code: PAYMENT_MODE_ERROR_CODES.FINANCED_REQUIRED, message: 'Modalità finanziata: importo finanziato maggiore di zero obbligatorio' });
  }
  if (count == null || !(count > 0)) {
    issues.push({ field: 'installments_count', code: PAYMENT_MODE_ERROR_CODES.FINANCED_REQUIRED, message: 'Modalità finanziata: numero rate obbligatorio' });
  }
  if (installment == null || !(installment > 0)) {
    issues.push({ field: 'installment_amount', code: PAYMENT_MODE_ERROR_CODES.FINANCED_REQUIRED, message: 'Modalità finanziata: importo rata obbligatorio' });
  }
  if (issues.length > 0) return issues;
  // Da qui i tre valori sono presenti e positivi (narrowing sopra).
  const fin = financedAmount as number;
  const n = count as number;
  const rata = installment as number;
  const planned = n * rata;
  const installmentToleranceCents = toCents(COMPOSITION_INSTALLMENT_TOLERANCE_PER_INSTALLMENT) * n;
  if (Math.abs(toCents(fin) - toCents(planned)) > installmentToleranceCents) {
    issues.push({
      field: 'installment_amount',
      code: PAYMENT_MODE_ERROR_CODES.FINANCED_MISMATCH,
      message: `Importo finanziato € ${euro(fin)} diverso da rate × rata (${n} × € ${euro(rata)} = € ${euro(planned)}; tolleranza € ${euro(COMPOSITION_INSTALLMENT_TOLERANCE_PER_INSTALLMENT)} per rata)`,
    });
  }
  const net = compositionProductNetPrice(p);
  const advance = p.device_advance_amount ?? 0;
  if (Math.abs(toCents(net) - toCents(advance + fin)) > toCents(COMPOSITION_PRICE_TOLERANCE)) {
    issues.push({
      field: 'financed_amount',
      code: PAYMENT_MODE_ERROR_CODES.FINANCED_MISMATCH,
      message: `Prezzo netto del componente € ${euro(net)} diverso da anticipo + finanziato (€ ${euro(advance)} + € ${euro(fin)} = € ${euro(advance + fin)})`,
    });
  }
  return issues;
}

/**
 * Schema dei componenti per una composizione finanziata (bundle con modalità)
 * o no (device_promo). Issue con `path = [indice, campo]`.
 */
export function compositionProductsSchemaFor(financed: boolean) {
  return z.array(CompositionProductInputSchema).min(1, 'Almeno un componente richiesto').superRefine((products, ctx) => {
    products.forEach((product, index) => {
      for (const issue of compositionProductCreditIssues(product, financed)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [index, issue.field], message: issue.message, params: { code: issue.code } });
      }
    });
  });
}

export const CreateCompositionSchema = z.object({
  canvas_item_id: z.string().uuid().nullable().optional(),
  /** Solo listini bundle (obbligatoria lì, vietata altrove: gate server-side per tipo listino). */
  payment_mode_id: z.string().uuid().nullable().optional(),
  financing_partner_id: z.string().uuid().nullable().optional(),
  /** Addebito effettivo (obbligatorio con modalità, tra gli ammessi dal template). */
  installment_repayment: z.enum(INSTALLMENT_REPAYMENTS).nullable().optional(),
  name: z.string().trim().max(160).nullable().optional(),
  display_order: z.number().int().optional(),
  is_active: z.boolean().optional(),
  products: z.array(CompositionProductInputSchema).min(1, 'Almeno un componente richiesto'),
});
export type CreateCompositionInput = z.infer<typeof CreateCompositionSchema>;

export const UpdateCompositionSchema = z.object({
  canvas_item_id: z.string().uuid().nullable().optional(),
  payment_mode_id: z.string().uuid().nullable().optional(),
  financing_partner_id: z.string().uuid().nullable().optional(),
  installment_repayment: z.enum(INSTALLMENT_REPAYMENTS).nullable().optional(),
  name: z.string().trim().max(160).nullable().optional(),
  display_order: z.number().int().optional(),
  is_active: z.boolean().optional(),
  products: z.array(CompositionProductInputSchema).min(1).optional(),
});
export type UpdateCompositionInput = z.infer<typeof UpdateCompositionSchema>;

/** Registro attese amministrative (`pos_admin_expectations`). */
export interface AdminExpectation {
  id: string;
  tenant_id: string;
  sale_id: string;
  sale_detail_id: string | null;
  composition_product_id: string | null;
  kind: AdminExpectationKind;
  expected_amount: string;
  status: AdminExpectationStatus;
  financing_partner_id: string | null;
  financing_contract_id: string | null;
  document_ref: string | null;
  documented_at: string | null;
  reconciled_at: string | null;
  cancelled_at: string | null;
  cancel_reason: string | null;
  notes: string | null;
  /** Controparte fornitore (nota di credito attesa da promozione prodotto); alternativa all'ente. */
  supplier_id: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Task #4322 — voce della lista amministrazione attese: attesa + contesto
 * della riga di vendita (modalità snapshot, controparte ente o fornitore,
 * pratica collegata, promo di origine).
 */
export interface AdminExpectationListItem extends AdminExpectation {
  sale_number: string | null;
  sale_date: string | null;
  product_name: string | null;
  quantity: string | null;
  payment_mode_id: string | null;
  payment_mode_code: string | null;
  payment_mode_name: string | null;
  financing_partner_name: string | null;
  supplier_name: string | null;
  /** Snapshot della riga: nome composizione e listino di origine (promo). */
  composition_name: string | null;
  price_list_name: string | null;
  financing_contract_cir: string | null;
  financing_contract_status: string | null;
}

export const AdminExpectationsListQuerySchema = z.object({
  status: z.enum(ADMIN_EXPECTATION_STATUSES).optional(),
  kind: z.enum(ADMIN_EXPECTATION_KINDS).optional(),
  sale_id: z.string().uuid().optional(),
  payment_mode_id: z.string().uuid().optional(),
  financing_partner_id: z.string().uuid().optional(),
  supplier_id: z.string().uuid().optional(),
});
export type AdminExpectationsListQuery = z.infer<typeof AdminExpectationsListQuerySchema>;

export const UpdateAdminExpectationStatusSchema = z.object({
  status: z.enum(ADMIN_EXPECTATION_OPERATOR_STATUSES),
  document_ref: z.string().trim().max(200).nullable().optional(),
  notes: z.string().trim().nullable().optional(),
});
export type UpdateAdminExpectationStatusInput = z.infer<typeof UpdateAdminExpectationStatusSchema>;

const PriceCoercion = z.union([
  z.number().finite(),
  z.string().trim().regex(/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/, 'Numero non valido'),
]).refine(value => Number.isFinite(Number(value)), 'Numero non finito');

export const UpsertPriceListItemSchema = z.object({
  product_id: z.string().min(1, 'Prodotto obbligatorio'),
  price: PriceCoercion.refine(value => Number(value) >= 0 && Math.round(Number(value) * 10000) < 1e12, 'Prezzo non valido o non rappresentabile'),
  discount_percent: PriceCoercion.refine(value => Number(value) >= 0 && Number(value) <= 100, 'Sconto fuori intervallo 0–100').optional().nullable(),
  supplier_id: NullableString,
  supplier_sku: z.string().trim().max(120).nullable().optional(),
  /** Task #3324 — faccia SdI (master): obbligatoria su ogni riga listino. */
  vat_rate_id: z.string().trim().min(1, 'Aliquota IVA SdI obbligatoria'),
  /** Task #3324 — riferimento normativo SdI (obbligatorio con aliquota zero). */
  sdi_rif_id: z.number().int().positive().nullable().optional(),
  max_discount_percent: z.union([z.number(), z.string()]).nullable().optional(),
});
export type UpsertPriceListItemInput = z.infer<typeof UpsertPriceListItemSchema>;

export const BulkCustomerActionSchema = z.object({
  customer_ids: z.array(z.string().uuid()).default([]),
  action: z.enum(['assign', 'remove']).default('assign'),
});
export type BulkCustomerActionInput = z.infer<typeof BulkCustomerActionSchema>;
